/**
 * Thrown by `permissionMiddleware` when a signed-in user's roles do not grant
 * the permission a server function needs.
 *
 * Lives here, next to the classifier that has to recognise it, so the throw
 * site, the redirect that handles it, and `isAuthError` below all agree on one
 * string instead of three copies of a literal drifting apart.
 */
export const ADMIN_ACCESS_REQUIRED_MESSAGE = "Admin access required";

/**
 * Detects errors caused by an expired or missing auth session, or by a session
 * that is valid but not allowed to see what it asked for.
 *
 * Server fns in this app surface auth failures in three shapes:
 * - `throw new Error("Authentication required")` from `authMiddleware`.
 * - `throw new Error("No coach found (403)")` from `requireClerkCoachAuth`,
 *   which arrives on the client as an Error whose message embeds the status
 *   code.
 * - `throw new Error(ADMIN_ACCESS_REQUIRED_MESSAGE)` from `permissionMiddleware`.
 *   It carries no status code, so it matched none of the shapes above and was
 *   filed as a production error — Sentry DCM-TN, a non-admin test account
 *   opening an `/admin/*` route. An authorization denial is an expected
 *   outcome, not a defect.
 *
 * Pure utility — safe to import from server or client code.
 */
export function isAuthError(error: unknown): boolean {
  if (!error) return false;

  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    if (msg.includes("authentication required")) return true;
    if (msg.includes(ADMIN_ACCESS_REQUIRED_MESSAGE.toLowerCase())) return true;
    if (msg.includes("unauthorized")) return true;
    if (/(?:^|\D)(401|403)(?:\D|$)/.test(msg)) return true;
  }

  if (typeof error === "object" && error !== null) {
    if ("status" in error && isAuthStatus(error.status)) return true;
    if ("statusCode" in error && isAuthStatus(error.statusCode)) return true;
  }

  return false;
}

function isAuthStatus(value: unknown): boolean {
  return value === 401 || value === 403;
}

/**
 * The messages this app's own guards throw when the *caller of the current
 * request* is not allowed in. Anchored at the start of the message so a
 * vendor error that merely quotes one of these phrases cannot match.
 *
 * - `authMiddleware` / `permission-middleware` / `auth-helpers`:
 *   `"Authentication required"`, sometimes with a suffix
 *   (`": Not authenticated as a Coach or a User."`, `" (307)"`).
 * - `permissionMiddleware`: {@link ADMIN_ACCESS_REQUIRED_MESSAGE}.
 * - `requireClerkCoachAuth` in `~/lib/coach-middleware`: `"No coach found
 *   (403)"` — a plain Error rather than a Response so server-fn results stay
 *   seroval-serializable.
 */
const INBOUND_AUTH_MESSAGES = [
  /^authentication required\b/,
  /^admin access required$/,
  /^no coach found \(403\)$/,
];

/**
 * Server-side counterpart to {@link isAuthError}, for the Worker's
 * `beforeSend` drop filter.
 *
 * `isAuthError` is a *client* classifier: it decides whether the router should
 * retry a failed server-fn call and refresh the session. Over there, every
 * error it sees is about this browser's own session, so recognising auth by
 * substring (`"unauthorized"`, a bare `401`/`403` in the message) and by a
 * `status`/`statusCode` field is fine.
 *
 * In the Worker those same heuristics match the wrong thing. Errors we throw
 * *ourselves* about an inbound caller sit next to errors thrown *at us* by the
 * APIs we call, and a `GhlApiError` for `GHL 401 Unauthorized` matches on all
 * three counts (see `~/lib/ghl/ghl-api-error`) — as does a Stripe
 * `statusCode: 401` or a Clerk 403. So `beforeSend` was dropping precisely the
 * events that say an integration credential has expired: an outbound 401 is
 * never noise, it means a whole class of work has silently stopped. The GHL
 * tag push behind `recordEvent` could fail on eight consecutive
 * attempts, dead-letter itself, and leave no error in Sentry at all — only the
 * daily abandoned-backlog report that has no idea why (DCM-V1).
 *
 * This predicate therefore recognises only the inbound shapes, by their exact
 * messages, and treats an `Error` that carries an HTTP status as an API-client
 * failure to be filed rather than dropped. A thrown `Response` — what
 * `Response.json({...}, { status: 403 })` produces inside our own route
 * guards — still counts as inbound.
 *
 * If a new inbound-noise source appears, add it here or to `ignoreErrors`;
 * do not widen this back into a substring match.
 */
export function isInboundAuthError(error: unknown): boolean {
  if (!error) return false;

  if (error instanceof Response) return isAuthStatus(error.status);

  if (!(error instanceof Error)) return false;

  // An Error carrying an HTTP status came from a client we called, not from a
  // guard of ours. Its 401/403 is an incident report, not caller noise.
  if ("status" in error || "statusCode" in error) return false;

  const message = error.message.trim().toLowerCase();
  return INBOUND_AUTH_MESSAGES.some((pattern) => pattern.test(message));
}
